import { useLatestSession } from 'src/hooks/useSession'
import SimpleLayout from '../../../layouts/SimpleLayout'
import Docs from 'src/components/Docs'
import { useCallback, useEffect, useState } from 'react'
import { LanguageCode } from 'src/models/language'
import useDocs from 'src/hooks/useDocs'
import Spinner from 'src/components/Spinner'
import LanguageSelectBar from 'src/components/LanguageSelectBar'
import { useParams } from 'react-router-dom'
import useOrg from 'src/hooks/useOrg'
import OrgBanner from 'src/components/OrgBanner'
import { Clipboard, ClipboardCheck } from 'lucide-react'
import { cn } from 'src/utils'

const Copyable = ({
  text,
  value,
}: {
  text: string
  value: string
}) => {
  const [isCopied, setIsCopied] = useState<boolean>(false)

  const copy = useCallback(() => {
    navigator.clipboard.writeText(value)
    setIsCopied(true)

    setTimeout(() => {
      setIsCopied(false)
    }, 1000)
  }, [value])

  return (
    <div
      className="hover:cursor flex select-none items-center gap-2 overflow-hidden rounded-sm p-2 text-sm transition-all ease-in-out hover:cursor-pointer hover:bg-slate-200"
      onClick={copy}
    >
      <p className="flex items-center">{text}</p>
      {isCopied && <ClipboardCheck fontSize={0.5} />}
      {!isCopied && <Clipboard fontSize={0.5} />}
    </div>
  )
}

const OrgPage = () => {
  let { orgId } = useParams()
  const { latestSession } = useLatestSession()

  const { org, isLoading: isOrgLoading } = useOrg(orgId!)

  const [fromLang, setFromLang] = useState<LanguageCode>(
    LanguageCode.SpanishSpain,
  )
  const [toLang, setToLang] = useState<LanguageCode>(
    LanguageCode.EnglishUs,
  )

  useEffect(() => {
    if (!latestSession) return
    setToLang(latestSession?.toLang)
    setFromLang(latestSession?.fromLang)
  }, [latestSession])

  const { docs, isNextLoading, isLoading } = useDocs({
    fromLang,
    toLang,
    group: 'orgs',
    groupId: orgId,
  })

  return (
    <SimpleLayout
      className="justify-start"
      footer={
        !isLoading &&
        org?.code && (
          <Copyable
            text="Sign-up Link"
            value={`${window.location.origin}/orgs/${org.id}/sign-up/${org.code}`}
          />
        )
      }
    >
      {(isLoading || isOrgLoading) && (
        <div className="min-h-4 mx-auto mb-4 block">
          <Spinner />
        </div>
      )}

      {!isLoading && !isOrgLoading && org && (
        <div className="mb-8 w-full max-w-sm">
          <OrgBanner {...org} />
        </div>
      )}

      {!isLoading && (
        <div className="mb-12 flex w-full max-w-sm justify-center">
          <LanguageSelectBar
            fromLang={fromLang}
            toLang={toLang}
            setFromLang={setFromLang}
            setToLang={setToLang}
          />
        </div>
      )}

      {!isLoading && (
        <section className="w-full max-w-sm">
          {docs && (
            <Docs
              docs={docs}
              fromLang={fromLang}
              toLang={toLang}
            />
          )}
        </section>
      )}

      <div
        className={cn(
          'mx-auto mb-4 w-fit transition-all ease-in-out',
          !isNextLoading && 'h-0 opacity-0',
          isNextLoading && 'h-4 opacity-100',
        )}
      >
        <Spinner />
      </div>
    </SimpleLayout>
  )
}

export default OrgPage
